import { useEffect, useMemo, useState } from 'react';
import { codeToCountryName } from '../utils/completeCountryMapping';
import LineChart from './LineChart';

const RegionPopup = ({ region, data, onClose, onBackToMap, onIpTypeChange, currentIpType = 'ipv4', globalDate }) => {
    const [activeTab, setActiveTab] = useState(currentIpType);

    // Synchronize with the currentIpType when it changes from the parent
    useEffect(() => {
        setActiveTab(currentIpType);
    }, [currentIpType]);

    // Function to change tab and notify parent component
    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        if (onIpTypeChange) {
            onIpTypeChange(newTab);
        }
    };

    // Function to get country names using the complete mapping
    const getCountryName = (code) => {
        return codeToCountryName[code] || code;
    };

    // Calculate regional Y-axis limits separately for IPv4 and IPv6
    const regionalLimits = useMemo(() => {
        const regionData = data?.[region];
        if (!regionData) return {
            ipv4: { min: 0, max: 100 },
            ipv6: { min: 0, max: 100 },
            firstEpoch: Date.now() / 1000
        };

        let ipv4Max = 0;
        let ipv6Max = 0;
        let globalFirstEpoch = Infinity;

        // Find the earliest epoch and maximum values for each IP type separately
        Object.values(regionData).forEach(countryData => {
            ['ipv4', 'ipv6'].forEach(ipType => {
                const ipTypeData = countryData[ipType];
                if (ipTypeData) {
                    Object.entries(ipTypeData).forEach(([epoch, value]) => {
                        const numEpoch = parseInt(epoch);
                        const numValue = parseFloat(value) || 0;

                        if (numEpoch < globalFirstEpoch) {
                            globalFirstEpoch = numEpoch;
                        }

                        if (ipType === 'ipv4' && numValue > ipv4Max) {
                            ipv4Max = numValue;
                        } else if (ipType === 'ipv6' && numValue > ipv6Max) {
                            ipv6Max = numValue;
                        }
                    });
                }
            });
        });

        // Add 10% padding to the max values
        const ipv4PaddedMax = ipv4Max * 1.1;
        const ipv6PaddedMax = ipv6Max * 1.1;

        return {
            ipv4: { min: 0, max: ipv4PaddedMax },
            ipv6: { min: 0, max: ipv6PaddedMax },
            firstEpoch: globalFirstEpoch === Infinity ? Date.now() / 1000 : globalFirstEpoch
        };
    }, [data, region]);

    // Process region data with first epoch normalization
    const processedData = useMemo(() => {
        const regionData = data?.[region];
        if (!regionData) return { countries: {}, stats: { ipv4: 0, ipv6: 0 } };

        const processedCountries = {};
        let ipv4Count = 0;
        let ipv6Count = 0;

        // Use the first epoch from regional limits
        const firstEpoch = regionalLimits.firstEpoch;

        Object.entries(regionData).forEach(([countryCode, countryData]) => {
            processedCountries[countryCode] = {
                ipv4: {},
                ipv6: {}
            };

            ['ipv4', 'ipv6'].forEach(ipType => {
                if (countryData[ipType] && Object.keys(countryData[ipType]).length > 0) {
                    // Copy existing data
                    processedCountries[countryCode][ipType] = { ...countryData[ipType] };

                    // Add initial point with value 0 if it doesn't exist at first epoch
                    if (!processedCountries[countryCode][ipType][firstEpoch]) {
                        processedCountries[countryCode][ipType][firstEpoch] = 0;
                    }

                    if (ipType === 'ipv4') ipv4Count++;
                    else ipv6Count++;
                }
            });
        });

        return {
            countries: processedCountries,
            stats: { ipv4: ipv4Count, ipv6: ipv6Count }
        };
    }, [data, region, regionalLimits]);

    // Function to find the first date where the value stops being 0
    const getStartDate = (countryData, ipType) => {
        if (!countryData[ipType]) return null;

        const entries = Object.entries(countryData[ipType])
            .map(([epoch, value]) => [parseInt(epoch), parseFloat(value) || 0])
            .sort((a, b) => a[0] - b[0]); // Ordenar por fecha

        // Find the first date where the value > 0
        const startEntry = entries.find(([epoch, value]) => value > 0);
        return startEntry ? { epoch: startEntry[0], value: startEntry[1] } : null;
    };

    // Sort countries by start date and then by initial value
    const countries = useMemo(() => {
        return Object.keys(processedData.countries).sort((countryA, countryB) => {
            const dataA = processedData.countries[countryA];
            const dataB = processedData.countries[countryB];

            // Get start date for both countries (use active tab)
            const startA = getStartDate(dataA, activeTab);
            const startB = getStartDate(dataB, activeTab);

            // If a country has no data, it goes to the end
            if (!startA && !startB) return 0;
            if (!startA) return 1;
            if (!startB) return -1;

            // Primera prioridad: ordenar por fecha de inicio
            if (startA.epoch !== startB.epoch) {
                return startA.epoch - startB.epoch;
            }

            // Segunda prioridad: si mismo fecha de inicio, ordenar por valor inicial (mayor primero)
            return startB.value - startA.value;
        });
    }, [processedData.countries, activeTab]);

    const currentData = processedData.countries;

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible'
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: '24px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#f8fafc',
                    position: 'relative'
                }}
            >
                <div>
                    <h2 style={{
                        margin: 0,
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#1f2937',
                        marginBottom: '4px'
                    }}>
                        {region} Data
                    </h2>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        onClick={onBackToMap}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#002766',
                            color: 'white',
                            border: 'none',
                            borderRadius: '25px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#008EFB';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#002766';
                        }}
                    >
                        ← Back to Map
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div
                style={{
                    display: 'flex',
                    backgroundColor: '#f1f5f9',
                    borderBottom: '1px solid #e2e8f0'
                }}
            >
                <button
                    onClick={() => handleTabChange('ipv4')}
                    style={{
                        flex: 1,
                        padding: '16px 24px',
                        border: 'none',
                        backgroundColor: activeTab === 'ipv4' ? '#ffffff' : 'transparent',
                        color: activeTab === 'ipv4' ? '#1e40af' : '#64748b',
                        fontSize: '16px',
                        fontWeight: activeTab === 'ipv4' ? '600' : '500',
                        cursor: 'pointer',
                        borderBottom: activeTab === 'ipv4' ? '3px solid #3b82f6' : '3px solid transparent',
                        transition: 'all 0.2s ease'
                    }}
                >
                    IPv4 ({processedData.stats.ipv4} countries)
                </button>
                <button
                    onClick={() => handleTabChange('ipv6')}
                    style={{
                        flex: 1,
                        padding: '16px 24px',
                        border: 'none',
                        backgroundColor: activeTab === 'ipv6' ? '#ffffff' : 'transparent',
                        color: activeTab === 'ipv6' ? '#1e40af' : '#64748b',
                        fontSize: '16px',
                        fontWeight: activeTab === 'ipv6' ? '600' : '500',
                        cursor: 'pointer',
                        borderBottom: activeTab === 'ipv6' ? '3px solid #3b82f6' : '3px solid transparent',
                        transition: 'all 0.2s ease'
                    }}
                >
                    IPv6 ({processedData.stats.ipv6} countries)
                </button>
            </div>

            {/* Content with scroll */}
            <div
                style={{
                    flex: 1,
                    overflow: 'visible',
                    padding: '24px',
                    backgroundColor: '#ffffff'
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '15px',
                        paddingBottom: '20px'
                    }}
                >
                    {countries
                        .filter(countryCode => {
                            const countryData = currentData[countryCode];
                            return countryData && countryData[activeTab] && Object.keys(countryData[activeTab]).length > 0;
                        })
                        .map(countryCode => {
                            const countryData = currentData[countryCode][activeTab];
                            const countryName = getCountryName(countryCode);

                            return (
                                <div
                                    key={`${countryCode}-${activeTab}`}
                                >
                                    <LineChart
                                        data={countryData}
                                        title={`${countryName} - ${activeTab === 'ipv4' ? 'IPv4' : 'IPv6'}`}
                                        ipType={activeTab}
                                        globalLimits={regionalLimits[activeTab]}
                                        discover={true}
                                        globalDate={globalDate}
                                    />
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

export default RegionPopup;
